import React from "react";
import { EMPTY, pyRound, toNum } from "../../../../utils/perfFormat";
import { usePhoneTheme } from "../../useIsPhone";
import { LegendRow } from "../LegendItem";
import { mixColors } from "./chartTokens";

// A 100% stacked bar (the scoring mix: where the points come from), each
// segment labelled inside ("61% 2PT") when it is wide enough. One colour
// scheme on both pages: 2PT bm-ink, 3PT bm-orange, FT bm-orange at 40%
// (chartTokens.MIX), picked from the segment key unless a colour is given.
// CSS bars like the profile's ScoringBreakdown, with role="img" and the
// whole mix in its aria-label.
//
//   StackBar({ segments, ariaLabel, legend, legendLabel, phone, className })
//
// segments  [{ key, label, share, color, textColor }]: `share` is a
//           percentage (null counts as 0 and is listed as "–").
// legend    adds a legend row (a list named `legendLabel`) under the bar
//           with every share in words, for a mix whose small segments have
//           no room for a label.
// Desktop: 40px high with 12px corners; phones 34px with 8px corners, and
// a label needs a little more room before it shows.

const cx = (...parts) => parts.filter(Boolean).join(" ");

const shareText = (s) => {
  const v = toNum(s.share);
  return v === null ? `${EMPTY} ${s.label}` : `${pyRound(v)}% ${s.label}`;
};

export const StackBar = ({ segments = [], ariaLabel, legend = false, legendLabel = "Scoring mix", phone: phoneProp, className = "" }) => {
  const themePhone = usePhoneTheme();
  const phone = phoneProp ?? themePhone;
  if (!segments.length) return null;
  const total = segments.reduce((sum, s) => sum + Math.max(0, toNum(s.share) || 0), 0);
  const scale = total > 100 ? 100 / total : 1;
  const minLabel = phone ? 14 : 10;
  const summary = ariaLabel || segments.map(shareText).join(", ");
  return (
    <div className={cx("flex flex-col gap-2", className)}>
      <div
        role="img"
        aria-label={summary}
        className={cx("flex w-full overflow-hidden bg-bm-mutedbg", phone ? "h-[34px] rounded-[8px]" : "h-10 rounded-[12px]")}
      >
        {segments.map((s, i) => {
          const share = Math.max(0, toNum(s.share) || 0) * scale;
          if (share <= 0) return null;
          const colors = mixColors(s.key);
          return (
            <div
              key={s.key ?? i}
              aria-hidden="true"
              className={cx("flex min-w-0 items-center justify-center overflow-hidden whitespace-nowrap font-archivo font-bold", phone ? "text-[12px]" : "text-[13px]")}
              style={{ width: `${share}%`, background: s.color || colors.color, color: s.textColor || colors.text }}
            >
              {share >= minLabel ? shareText(s) : null}
            </div>
          );
        })}
      </div>
      {legend && (
        <LegendRow
          label={legendLabel}
          items={segments.map((s) => ({ key: s.key, swatch: "bar", color: s.color || mixColors(s.key).color, label: shareText(s) }))}
        />
      )}
    </div>
  );
};

export default StackBar;
